/**
 * Hindi.
 *
 * Keyed by the English source string, so anything missing here falls back to
 * the English already on screen rather than to a key name. That is what lets a
 * partly translated app be a working app.
 *
 * The register a school actually keeps is bilingual — the ledger is in English
 * and the conversation over it is in Hindi — so a few terms are deliberately
 * left as they are said out loud rather than translated to a word nobody uses:
 * "UPI", "PDF", "CSV", "AI". Translating those makes a screen *more* foreign,
 * not less. Money and dates are formatted by locale elsewhere and are not here.
 */

export const hi: Record<string, string> = {
  // --- chrome, navigation, portals -----------------------------------------
  "Skip to content": "मुख्य सामग्री पर जाएँ",
  "Toggle navigation": "मेन्यू खोलें/बंद करें",
  "Sign out": "साइन आउट",
  "Sign in": "साइन इन",
  "Sign in to your school": "अपने स्कूल में साइन इन करें",
  "Sign-in failed": "साइन इन नहीं हो सका",
  "Signing you in": "साइन इन किया जा रहा है",
  "Restoring your session": "आपका सत्र बहाल किया जा रहा है",
  Language: "भाषा",
  Administration: "प्रशासन",
  Teaching: "शिक्षण",
  Family: "परिवार",
  "Parent portal": "अभिभावक पोर्टल",
  "Student portal login": "विद्यार्थी पोर्टल लॉगिन",
  "Payment overdue": "भुगतान बकाया",
  "Not built yet": "अभी बना नहीं है",

  // Sidebar sections
  Overview: "सार",
  People: "लोग",
  Academics: "शैक्षणिक",
  Finance: "वित्त",
  Communication: "संचार",
  System: "सिस्टम",
  Today: "आज",
  "My classes": "मेरी कक्षाएँ",
  "My family": "मेरा परिवार",
  You: "आप",
  AI: "एआई",

  // Sidebar items
  Dashboard: "डैशबोर्ड",
  "School profile": "स्कूल की जानकारी",
  "Users & access": "उपयोगकर्ता और पहुँच",
  Students: "विद्यार्थी",
  "Import students": "विद्यार्थी आयात करें",
  Guardians: "अभिभावक",
  Staff: "कर्मचारी",
  Admissions: "दाखिले",
  "Classes & sections": "कक्षाएँ और सेक्शन",
  Attendance: "हाज़िरी",
  Timetable: "समय-सारणी",
  Examinations: "परीक्षाएँ",
  Transport: "परिवहन",
  Fees: "फ़ीस",
  "Fee structures": "फ़ीस संरचना",
  "Salary register": "वेतन रजिस्टर",
  "My payslips": "मेरी वेतन पर्चियाँ",
  Noticeboard: "सूचना पट्ट",
  Notifications: "सूचनाएँ",
  "Audit trail": "अंकेक्षण रिकॉर्ड",
  "Platform status": "प्लेटफ़ॉर्म स्थिति",
  "Ask about your school": "अपने स्कूल के बारे में पूछें",
  "Ask a question": "सवाल पूछें",
  "AI suggestions": "एआई सुझाव",
  "AI settings": "एआई सेटिंग",
  "Teaching assistant": "शिक्षण सहायक",
  "Mark attendance": "हाज़िरी लगाएँ",
  "My sections": "मेरे सेक्शन",
  "Marks entry": "अंक भरें",
  Results: "परिणाम",
  Inbox: "इनबॉक्स",
  "Academic setup": "शैक्षणिक व्यवस्था",
  "Message delivery": "संदेश वितरण",

  // Roles
  "School Admin": "स्कूल प्रशासक",
  Principal: "प्रधानाचार्य",
  Teacher: "शिक्षक",
  "Class Teacher": "कक्षा शिक्षक",
  Accountant: "लेखाकार",
  Receptionist: "रिसेप्शनिस्ट",
  Parent: "अभिभावक",
  Student: "विद्यार्थी",

  // --- common words --------------------------------------------------------
  Name: "नाम",
  "Full name": "पूरा नाम",
  "First name": "पहला नाम",
  "Middle name": "मध्य नाम",
  "Last name": "अंतिम नाम",
  Email: "ईमेल",
  Phone: "फ़ोन",
  Password: "पासवर्ड",
  "Email or phone": "ईमेल या फ़ोन",
  Gender: "लिंग",
  Male: "पुरुष",
  Female: "महिला",
  "Not disclosed": "नहीं बताया",
  "Date of birth": "जन्म तिथि",
  "Blood group": "रक्त समूह",
  City: "शहर",
  State: "राज्य",
  "Postal code": "पिन कोड",
  Class: "कक्षा",
  Section: "सेक्शन",
  Sections: "सेक्शन",
  Subject: "विषय",
  Subjects: "विषय",
  Grade: "कक्षा",
  Term: "सत्र",
  Year: "वर्ष",
  "Academic year": "शैक्षणिक वर्ष",
  Date: "दिनांक",
  Month: "महीना",
  Status: "स्थिति",
  Reason: "कारण",
  Total: "कुल",
  Amount: "राशि",
  Method: "तरीका",
  Reference: "संदर्भ",
  Balance: "शेष",
  Details: "विवरण",
  Title: "शीर्षक",
  Notice: "सूचना",
  Actions: "कार्रवाई",
  Category: "श्रेणी",
  Code: "कोड",
  Order: "क्रम",
  Contact: "संपर्क",
  Relationship: "रिश्ता",
  Designation: "पदनाम",
  Department: "विभाग",
  Qualification: "योग्यता",
  Qualifications: "योग्यताएँ",
  Institution: "संस्थान",
  Employment: "नियुक्ति",
  "Employment type": "नियुक्ति का प्रकार",
  "Employment status": "नियुक्ति की स्थिति",
  "Joining date": "कार्यभार तिथि",
  "Emergency contact": "आपात संपर्क",
  "Emergency phone": "आपात फ़ोन",
  "Medical notes": "चिकित्सा टिप्पणी",
  "Previous school": "पिछला स्कूल",
  "Primary contact": "मुख्य संपर्क",
  Guardian: "अभिभावक",
  Children: "बच्चे",
  Child: "बच्चा",
  Person: "व्यक्ति",
  Roles: "भूमिकाएँ",
  Documents: "दस्तावेज़",
  Certificates: "प्रमाणपत्र",
  Salary: "वेतन",
  Payslip: "वेतन पर्ची",
  Payslips: "वेतन पर्चियाँ",
  Deductions: "कटौती",
  Gross: "सकल",
  Net: "शुद्ध",
  "Net payable": "देय शुद्ध राशि",
  "Basic pay": "मूल वेतन",
  Allowance: "भत्ता",
  "Working days": "कार्य दिवस",
  "Unpaid days": "अवैतनिक दिन",
  Route: "मार्ग",
  Routes: "मार्ग",
  Stop: "पड़ाव",
  Bus: "बस",
  Seats: "सीटें",
  Riders: "सवार",
  Capacity: "क्षमता",
  Pickup: "पिकअप",
  Legs: "यात्रा",
  Charges: "शुल्क",
  Invoices: "बिल",
  Installments: "किश्तें",
  Outstanding: "बकाया",
  Overdue: "अवधि पार",
  Paid: "भुगतान हुआ",
  "Paid so far": "अब तक भुगतान",
  "Due now": "अभी देय",
  Draft: "मसौदा",
  Published: "प्रकाशित",
  Cancelled: "रद्द",
  Current: "वर्तमान",
  Currently: "वर्तमान में",
  Active: "सक्रिय",
  Linked: "जुड़ा हुआ",
  Invited: "आमंत्रित",
  Confidential: "गोपनीय",
  Awaited: "प्रतीक्षित",
  Present: "उपस्थित",
  Absent: "अनुपस्थित",
  Late: "देर से",
  Optional: "वैकल्पिक",
  Required: "आवश्यक",
  None: "कोई नहीं",
  Other: "अन्य",
  "No.": "क्र.",
  "Emp. no.": "कर्म. क्र.",
  "Fee / year": "फ़ीस / वर्ष",
  "Form fee": "फ़ॉर्म फ़ीस",
  "Form fees": "फ़ॉर्म फ़ीस",
  Enquiries: "पूछताछ",
  Applications: "आवेदन",
  Admitted: "दाखिला हुआ",
  "Seats offered": "दी गई सीटें",
  "Admitted this year": "इस वर्ष दाखिला",
  "On roll": "रजिस्टर पर",
  "This year": "इस वर्ष",
  "This month": "इस महीने",
  "This season": "इस सत्र",
  "Recent days": "पिछले दिन",
  "Payment history": "भुगतान इतिहास",
  "Earlier revisions": "पुराने संशोधन",
  "Earlier imports": "पिछले आयात",
  "Enrolment history": "नामांकन इतिहास",
  "Teaching assignments": "शिक्षण ज़िम्मेदारियाँ",
  Placement: "स्थान-निर्धारण",
  "Your access": "आपकी पहुँच",

  // --- buttons and actions -------------------------------------------------
  Add: "जोड़ें",
  Edit: "संपादित करें",
  Save: "सहेजें",
  Saved: "सहेजा गया",
  Cancel: "रद्द करें",
  Remove: "हटाएँ",
  Open: "खोलें",
  Download: "डाउनलोड",
  Print: "प्रिंट",
  Publish: "प्रकाशित करें",
  Next: "आगे",
  Previous: "पीछे",
  Back: "वापस",
  "Go back": "वापस जाएँ",
  "Go home": "मुखपृष्ठ पर जाएँ",
  Reload: "फिर से लोड करें",
  Clear: "साफ़ करें",
  Choose: "चुनें",
  "Choose…": "चुनें…",
  "See all": "सब देखें",
  Assign: "नियुक्त करें",
  Withdraw: "वापस लें",
  Reject: "अस्वीकार करें",
  "Add guardian": "अभिभावक जोड़ें",
  "Add section": "सेक्शन जोड़ें",
  "Add subject": "विषय जोड़ें",
  "Add term": "सत्र जोड़ें",
  "Add year": "वर्ष जोड़ें",
  "Add staff member": "कर्मचारी जोड़ें",
  "Add qualification": "योग्यता जोड़ें",
  "Admit student": "विद्यार्थी का दाखिला",
  "Admit the first one": "पहला दाखिला करें",
  "View all students": "सभी विद्यार्थी देखें",
  "Record payment": "भुगतान दर्ज करें",
  "Print receipt": "रसीद प्रिंट करें",
  "Issue invoice": "बिल जारी करें",
  "Issue a certificate": "प्रमाणपत्र जारी करें",
  "Mark paid": "भुगतान अंकित करें",
  "Mark read": "पढ़ा हुआ अंकित करें",
  "Mark all read": "सब पढ़ा हुआ अंकित करें",
  "Mark as awaited": "प्रतीक्षित अंकित करें",
  "Build a month": "महीना बनाएँ",
  "Apply unpaid leave and rebuild": "अवैतनिक छुट्टी लगाकर फिर से बनाएँ",
  "Take an enquiry": "पूछताछ दर्ज करें",
  "Offer a seat": "सीट प्रस्तावित करें",
  "Take ₹500 form fee": "₹500 फ़ॉर्म फ़ीस लें",
  "Choose a file": "फ़ाइल चुनें",
  "Upload a file": "फ़ाइल अपलोड करें",
  "Link login": "लॉगिन जोड़ें",
  "Link a login": "लॉगिन जोड़ें",
  "Link account": "खाता जोड़ें",
  "Detach login": "लॉगिन अलग करें",
  "Revoke access": "पहुँच वापस लें",
  "Invite someone": "किसी को आमंत्रित करें",
  "Send invitation": "आमंत्रण भेजें",
  "Save roles": "भूमिकाएँ सहेजें",
  "Save sheet": "शीट सहेजें",
  "Save draft": "मसौदा सहेजें",
  "Save as draft": "मसौदे के रूप में सहेजें",
  "Write a notice": "सूचना लिखें",
  "Take it down": "हटा दें",
  "Pin to the top": "ऊपर पिन करें",
  Pinned: "पिन किया",
  "Undo this import": "यह आयात पूर्ववत करें",
  Ask: "पूछें",
  Answer: "उत्तर",
  Question: "सवाल",
  "Your question": "आपका सवाल",
  "Set salary": "वेतन तय करें",
  "Revise salary": "वेतन संशोधित करें",
  "Save revision": "संशोधन सहेजें",
  "Approve register": "रजिस्टर मंज़ूर करें",
  "Record enquiry": "पूछताछ दर्ज करें",
  "+ Part cash, part online": "+ कुछ नक़द, कुछ ऑनलाइन",
  "End employment": "सेवा समाप्त करें",
  Offboard: "सेवामुक्त करें",
  "Record departure": "प्रस्थान दर्ज करें",

  // --- loading and empty ---------------------------------------------------
  Loading: "लोड हो रहा है",
  "One moment…": "एक क्षण…",
  Counting: "गिना जा रहा है",
  "Loading students": "विद्यार्थी लोड हो रहे हैं",
  "Loading student": "विद्यार्थी लोड हो रहा है",
  "Loading staff": "कर्मचारी लोड हो रहे हैं",
  "Loading guardians": "अभिभावक लोड हो रहे हैं",
  "Loading invoices": "बिल लोड हो रहे हैं",
  "Loading balances": "शेष राशि लोड हो रही है",
  "Loading payroll": "वेतन लोड हो रहा है",
  "Loading register": "रजिस्टर लोड हो रहा है",
  "Loading admissions": "दाखिले लोड हो रहे हैं",
  "Loading applications": "आवेदन लोड हो रहे हैं",
  "Loading enquiries": "पूछताछ लोड हो रही है",
  "Loading transport": "परिवहन लोड हो रहा है",
  "Loading roster": "सूची लोड हो रही है",
  "Loading salary": "वेतन लोड हो रहा है",
  "Loading your payslips": "आपकी वेतन पर्चियाँ लोड हो रही हैं",
  "Loading your sections": "आपके सेक्शन लोड हो रहे हैं",
  "Loading the timetable": "समय-सारणी लोड हो रही है",
  "Loading the register": "रजिस्टर लोड हो रहा है",
  "Loading results": "परिणाम लोड हो रहे हैं",
  "Nothing yet": "अभी कुछ नहीं",
  "Nothing yet.": "अभी कुछ नहीं।",
  "Nothing here yet": "यहाँ अभी कुछ नहीं",
  "Nothing outstanding": "कोई बकाया नहीं",
  "Nothing recorded": "कुछ दर्ज नहीं",
  "No documents": "कोई दस्तावेज़ नहीं",
  "None issued": "कोई जारी नहीं",
  "No invoices": "कोई बिल नहीं",
  "No invoices yet": "अभी कोई बिल नहीं",
  "No payments recorded yet": "अभी कोई भुगतान दर्ज नहीं",
  "No payslips yet": "अभी कोई वेतन पर्ची नहीं",
  "No registers yet": "अभी कोई रजिस्टर नहीं",
  "No salary set": "वेतन तय नहीं है",
  "No routes yet": "अभी कोई मार्ग नहीं",
  "No enquiries yet": "अभी कोई पूछताछ नहीं",
  "No applications yet": "अभी कोई आवेदन नहीं",
  "No students match": "कोई विद्यार्थी नहीं मिला",
  "No staff match": "कोई कर्मचारी नहीं मिला",
  "No guardians match": "कोई अभिभावक नहीं मिला",
  "No sections assigned": "कोई सेक्शन नहीं सौंपा गया",
  "No login": "कोई लॉगिन नहीं",
  "No login linked": "कोई लॉगिन जुड़ा नहीं",
  "No guardian": "कोई अभिभावक नहीं",
  "No children linked": "कोई बच्चा जुड़ा नहीं",
  "No children linked to your account": "आपके खाते से कोई बच्चा जुड़ा नहीं है",
  "We could not find your record": "आपका रिकॉर्ड नहीं मिला",
  "Nobody on this register": "इस रजिस्टर पर कोई नहीं",
  "Nobody on this bus yet": "इस बस पर अभी कोई नहीं",
  "Not taken": "नहीं ली गई",
  "Register taken": "हाज़िरी ली गई",
  "Payment not recorded": "भुगतान दर्ज नहीं",
  "Payment recorded": "भुगतान दर्ज हुआ",

  // --- errors --------------------------------------------------------------
  "Something went wrong": "कुछ गड़बड़ हुई",
  "Could not load": "लोड नहीं हो सका",
  "Could not load students": "विद्यार्थी लोड नहीं हो सके",
  "Could not load staff": "कर्मचारी लोड नहीं हो सके",
  "Could not load invoices": "बिल लोड नहीं हो सके",
  "Could not load balances": "शेष राशि लोड नहीं हो सकी",
  "Could not load this student": "यह विद्यार्थी लोड नहीं हो सका",
  "Page not found": "पेज नहीं मिला",
  "Please ask the school office to check this.":
    "कृपया स्कूल कार्यालय से जाँच करवाएँ।",
  "Give an email address or a phone number.": "ईमेल या फ़ोन नंबर दीजिए।",

  // --- searching and filters ----------------------------------------------
  "Search students": "विद्यार्थी खोजें",
  "Search staff": "कर्मचारी खोजें",
  "Search guardians": "अभिभावक खोजें",
  "Search by name or admission number…": "नाम या दाखिला नंबर से खोजें…",
  "Name, phone or email": "नाम, फ़ोन या ईमेल",
  "Name, employee number or designation": "नाम, कर्मचारी क्रमांक या पदनाम",
  "All classes": "सभी कक्षाएँ",
  "All sections": "सभी सेक्शन",
  "All terms": "सभी सत्र",
  "Every class": "हर कक्षा",
  "The whole school": "पूरा स्कूल",
  "By class": "कक्षा अनुसार",
  "Choose a section": "सेक्शन चुनें",
  "Choose a section…": "सेक्शन चुनें…",
  "Choose a route": "मार्ग चुनें",
  "Choose a school": "स्कूल चुनें",
  "Choose an account": "खाता चुनें",

  // --- the screens a school lives on ---------------------------------------
  "Signed in as": "साइन इन:",
  "School account": "स्कूल खाता",
  School: "स्कूल",
  Created: "बनाया गया",
  Address: "पता",
  Currency: "मुद्रा",
  Timezone: "समय क्षेत्र",
  Subscription: "सदस्यता",
  "Short code": "संक्षिप्त कोड",
  "Academic year starts": "शैक्षणिक वर्ष आरंभ",
  Authenticated: "प्रमाणित",
  "Permissions held": "प्राप्त अनुमतियाँ",
  Pagination: "पृष्ठ क्रम",
  All: "सभी",

  // Students
  "Admission no.": "दाखिला क्र.",
  "Admission date": "दाखिला तिथि",
  "Mother tongue": "मातृभाषा",
  "Attendance mode": "हाज़िरी का तरीका",
  "Displayed on the profile": "प्रोफ़ाइल पर दिखता है",
  "Allergies, conditions staff must know about":
    "एलर्जी या बीमारी जो कर्मचारियों को पता होनी चाहिए",
  "Optional — a student can be admitted and placed later":
    "वैकल्पिक — दाखिला पहले और सेक्शन बाद में भी दिया जा सकता है",
  "Added in the same request, so a student is never saved without them":
    "उसी अनुरोध में जुड़ता है, ताकि कोई बच्चा बिना अभिभावक के दर्ज न हो",
  "A student with no guardian cannot be contacted in an emergency.":
    "जिस बच्चे का कोई अभिभावक दर्ज नहीं, आपात स्थिति में उससे संपर्क नहीं हो सकता।",
  "No guardians on record": "कोई अभिभावक दर्ज नहीं",
  "No enrolments yet": "अभी कोई नामांकन नहीं",
  "Not placed in a section": "किसी सेक्शन में नहीं रखा गया",
  "One row per year and section": "हर वर्ष और सेक्शन के लिए एक पंक्ति",
  "Try clearing a filter, or admit your first student.":
    "कोई फ़िल्टर हटाकर देखिए, या पहला दाखिला कीजिए।",
  "Assign a section": "सेक्शन सौंपें",
  Graduated: "उत्तीर्ण",
  "Transferred out": "स्थानांतरित",
  Withdrawn: "नाम वापस",
  Left: "छोड़ चुके",
  Joined: "जुड़े",
  Employed: "कार्यरत",
  "On leave": "छुट्टी पर",
  Suspended: "निलंबित",
  Resigned: "इस्तीफ़ा",
  Retired: "सेवानिवृत्त",
  Terminated: "सेवा समाप्त",
  Inactive: "निष्क्रिय",
  "Has login": "लॉगिन है",
  "Employee number": "कर्मचारी क्रमांक",
  Experience: "अनुभव",
  "Prior experience (years)": "पिछला अनुभव (वर्ष)",
  "PGT Physics, Accountant, Nurse": "पीजीटी भौतिकी, लेखाकार, नर्स",
  "M.Sc Physics": "एम.एससी. भौतिकी",
  "So this person can sign in and be assigned":
    "ताकि यह व्यक्ति साइन इन कर सके और इन्हें ज़िम्मेदारी दी जा सke",
  "This person sees no student records.":
    "इन्हें किसी विद्यार्थी का रिकॉर्ड नहीं दिखता।",
  "Closes every teaching assignment immediately":
    "सारी शिक्षण ज़िम्मेदारियाँ तुरंत समाप्त कर देता है",
  "What a teacher can see is decided here":
    "शिक्षक को क्या दिखेगा, यह यहीं तय होता है",
  "Rights are per-guardian and enforced by the API, not by this page":
    "अधिकार हर अभिभावक के अलग हैं और सर्वर लागू करता है, यह पेज नहीं",

  // Certificates and documents
  "Transfer certificate": "स्थानांतरण प्रमाणपत्र",
  "Transfer certificate (previous school)":
    "स्थानांतरण प्रमाणपत्र (पिछला स्कूल)",
  "Bonafide certificate": "बोनाफ़ाइड प्रमाणपत्र",
  "Character certificate": "चरित्र प्रमाणपत्र",
  "Study certificate": "अध्ययन प्रमाणपत्र",
  "Birth certificate": "जन्म प्रमाणपत्र",
  Aadhaar: "आधार",
  "Caste certificate": "जाति प्रमाणपत्र",
  "Income certificate": "आय प्रमाणपत्र",
  "Medical record": "चिकित्सा रिकॉर्ड",
  Photograph: "फ़ोटो",
  "Report card (previous school)": "रिपोर्ट कार्ड (पिछला स्कूल)",
  "Numbered, recorded, and reprintable — what the counterfoil register was for.":
    "क्रमांकित, दर्ज, और दोबारा छापने लायक — जो काम काउंटरफ़ॉइल रजिस्टर करता था।",
  "Nothing has been certified about this student yet.":
    "इस विद्यार्थी के बारे में अभी कुछ प्रमाणित नहीं किया गया।",
  "Nothing has been collected from this family yet.":
    "इस परिवार से अभी कोई काग़ज़ नहीं लिया गया।",
  "Remarks (printed on the face of it)": "टिप्पणी (प्रमाणपत्र पर छपेगी)",
  "Conduct satisfactory": "आचरण संतोषजनक",
  "What is this?": "यह क्या है?",
  "PDF, JPEG or PNG. Confidential papers are hidden from roles without school-wide access.":
    "PDF, JPEG या PNG। गोपनीय काग़ज़ उन्हीं को दिखते हैं जिन्हें पूरे स्कूल की पहुँच है।",

  // Fees
  "Who owes what": "किस पर कितना बाकी",
  "All unpaid invoices": "सभी अदत्त बिल",
  "Past the due date": "नियत तिथि निकल चुकी",
  "Largest balance first — the list finance works through":
    "सबसे बड़ा बकाया पहले — यही सूची लेखा विभाग निपटाता है",
  "Every issued invoice has been settled.":
    "हर जारी बिल का निपटारा हो चुका है।",
  "The ledger does not balance": "खाता मेल नहीं खा रहा",
  "Generate invoices from a published fee structure.":
    "प्रकाशित फ़ीस संरचना से बिल बनाइए।",
  "Cheque number, UTR": "चेक नंबर, यूटीआर",

  // Payroll
  "Set up the components first": "पहले वेतन के घटक बनाइए",
  "Basic, HRA, conveyance, PF — the lines a payslip prints.":
    "मूल वेतन, एचआरए, यात्रा भत्ता, पीएफ — जो पंक्तियाँ वेतन पर्ची पर छपती हैं।",
  "Running it again is normal — a draft is rebuilt, never patched.":
    "दोबारा चलाना सामान्य है — मसौदा फिर से बनता है, उसमें सुधार नहीं होता।",
  "Summed over every row on the register, not over what is on screen.":
    "रजिस्टर की हर पंक्ति का जोड़, सिर्फ़ स्क्रीन पर दिखने वालों का नहीं।",
  Registers: "रजिस्टर",
  "Newest first.": "नए पहले।",
  "Set salaries on each staff profile, then build a month above.":
    "हर कर्मचारी की प्रोफ़ाइल पर वेतन तय कीजिए, फिर ऊपर से महीना बनाइए।",
  "Set a salary on a staff profile, then build the month again.":
    "किसी कर्मचारी की प्रोफ़ाइल पर वेतन तय कीजिए, फिर महीना दोबारा बनाइए।",
  "This person will be skipped when the month's register is built.":
    "महीने का रजिस्टर बनाते समय इन्हें छोड़ दिया जाएगा।",
  "Effective from": "इस तिथि से प्रभावी",
  "Effective-dated. A raise opens a new revision; old payslips keep their figures.":
    "तिथि से बँधा हुआ। बढ़ोतरी नया संशोधन खोलती है; पुरानी पर्चियों के आँकड़े वैसे ही रहते हैं।",
  "Annual increment": "वार्षिक वेतन वृद्धि",
  "A slip appears here once the school approves the month's register.":
    "जैसे ही स्कूल महीने का रजिस्टर मंज़ूर करता है, पर्ची यहाँ आ जाती है।",
  "Your own salary slips. Nobody else can see them here.":
    "आपकी अपनी वेतन पर्चियाँ। यहाँ इन्हें कोई और नहीं देख सकता।",

  // Admissions
  "What a committee asks in May, and no register can answer.":
    "मई में प्रबंध समिति जो पूछती है, और कोई रजिस्टर जिसका जवाब नहीं दे पाता।",
  "on the same day sheet as everything else":
    "बाकी सब के साथ उसी दिन के हिसाब में",
  "Thirty seconds at a counter. Everything else is optional.":
    "काउंटर पर तीस सेकंड। बाकी सब वैकल्पिक है।",
  "Class asked about": "किस कक्षा के बारे में पूछा",
  "Each form shows only what its state allows.":
    "हर फ़ॉर्म पर वही विकल्प दिखते हैं जो उसकी मौजूदा स्थिति में संभव हैं।",
  "An enquiry becomes an application when the family fills the form.":
    "जब परिवार फ़ॉर्म भरता है, पूछताछ आवेदन बन जाती है।",
  "Who asked, and what became of it.": "किसने पूछा, और उसका क्या हुआ।",
  "Record the next phone call and it appears here.":
    "अगली फ़ोन कॉल दर्ज कीजिए, वह यहाँ दिख जाएगी।",
  "How they found us": "हमारे बारे में कैसे पता चला",
  "Offer expires": "प्रस्ताव की अंतिम तिथि",
  "Grade 1": "कक्षा 1",

  // Transport
  "Papers to renew": "नवीनीकरण के काग़ज़",
  "Riders against seats.": "सीटों के मुक़ाबले सवार।",
  "Pick one above to see who is on it.":
    "ऊपर से एक चुनिए और देखिए उस पर कौन है।",
  "Assign a student to one of its stops.":
    "किसी विद्यार्थी को इसके किसी पड़ाव पर जोड़िए।",
  "Add a bus, a driver and a route to start putting children on them.":
    "बच्चों को बस पर बैठाने के लिए पहले बस, चालक और मार्ग जोड़िए।",

  // Attendance
  "Pick one of your sections to take the register.":
    "हाज़िरी लेने के लिए अपने किसी सेक्शन को चुनिए।",
  "Enrol students before taking a register.":
    "हाज़िरी लेने से पहले विद्यार्थियों का नामांकन कीजिए।",
  "No students in this section": "इस सेक्शन में कोई विद्यार्थी नहीं",
  "No attendance recorded yet": "अभी कोई हाज़िरी दर्ज नहीं",
  "Could not open the register": "रजिस्टर नहीं खुल सका",
  Excused: "अनुमति सहित",
  "Half day": "आधा दिन",
  "Everyone starts as present — mark only the exceptions.":
    "सब पहले से उपस्थित हैं — सिर्फ़ अपवाद अंकित कीजिए।",
  "Ask the school office to link your account to your child's record.":
    "स्कूल कार्यालय से कहिए कि आपका खाता आपके बच्चे के रिकॉर्ड से जोड़ दिया जाए।",
  "Results appear here once the school has released them.":
    "स्कूल जैसे ही परिणाम जारी करता है, वे यहाँ दिख जाते हैं।",
  "Nothing published yet": "अभी कुछ प्रकाशित नहीं",

  // Import
  "Nothing imported yet": "अभी कुछ आयात नहीं हुआ",
  "Nothing is created by uploading.": "सिर्फ़ अपलोड करने से कुछ नहीं बनता।",
  "Reading your file": "आपकी फ़ाइल पढ़ी जा रही है",
  "Ready to import": "आयात के लिए तैयार",
  "Rows found": "मिली पंक्तियाँ",
  "Need fixing": "सुधार चाहिए",
  "Row numbers match your spreadsheet, header row included.":
    "पंक्ति संख्याएँ आपकी शीट जैसी ही हैं, शीर्षक पंक्ति सहित।",
  "The file must have these.": "फ़ाइल में ये होने ही चाहिए।",
  "Used if present, ignored if not.":
    "हों तो लिए जाते हैं, न हों तो छोड़ दिए जाते हैं।",
  "This file could not be read": "यह फ़ाइल पढ़ी नहीं जा सकी",
  "What happens next": "आगे क्या होगा",
  "Work through the steps above in order.": "ऊपर के चरण क्रम से पूरे कीजिए।",

  // Dashboard / status
  "Foundation release": "आधार संस्करण",
  "Fetched through the authenticated API and scoped by the database":
    "प्रमाणित एपीआई से लिया गया और डेटाबेस द्वारा सीमित",
  "Resolved server-side on every request": "हर अनुरोध पर सर्वर तय करता है",
  "Recorded in the audit trail": "अंकेक्षण रिकॉर्ड में दर्ज",
  "Account replaced, left the school…": "खाता बदला, स्कूल छोड़ा…",
  "Reason type": "कारण का प्रकार",
  "Loading school": "स्कूल लोड हो रहा है",
  "Loading balance": "शेष लोड हो रहा है",
  "Loading staff member": "कर्मचारी लोड हो रहा है",
  "Could not load guardians": "अभिभावक लोड नहीं हो सके",
  "Could not load results": "परिणाम लोड नहीं हो सके",
  "Could not load the school": "स्कूल लोड नहीं हो सका",
  "Could not load this staff member": "यह कर्मचारी लोड नहीं हो सका",
  "students matching": "विद्यार्थी मिले",
  "Filtered to one student": "एक विद्यार्थी तक सीमित",
  "All issued invoices": "सभी जारी बिल",
  "A month is built, checked, approved, and then paid out. Deductions are recorded as the school computes them — this is not a statutory return.":
    "महीना बनता है, जाँचा जाता है, मंज़ूर होता है, फिर भुगतान होता है। कटौती वही दर्ज होती है जो स्कूल निकालता है — यह कोई सरकारी रिटर्न नहीं है।",
  "Collections and outstanding balances": "वसूली और बकाया शेष",
  "Enquiries, forms and decisions — the season before a child is a student.":
    "पूछताछ, फ़ॉर्म और फ़ैसले — बच्चे के विद्यार्थी बनने से पहले का मौसम।",
  "Buses, routes and who is on them. Putting a child on a route bills their family for it.":
    "बसें, मार्ग और उन पर कौन है। किसी बच्चे को मार्ग पर बैठाने से उसके परिवार को उसका बिल जाता है।",
  "Students on the roll, and how to find them.":
    "रजिस्टर पर दर्ज विद्यार्थी, और उन्हें कैसे खोजें।",
  "Ledger balanced": "खाता मेल खाता है",
  "on both sides.": "दोनों ओर।",
  "Balance due": "देय शेष",
  students: "विद्यार्थी",
  student: "विद्यार्थी",
  Invoice: "बिल",
  Receipt: "रसीद",
  Adjusted: "समायोजित",
  Issued: "जारी",
  Due: "देय",
  "Allocated to this invoice. A receipt is issued automatically — one receipt, however many ways the money arrived.":
    "इसी बिल में लगेगा। रसीद अपने आप बनती है — पैसा चाहे जितने तरीक़ों से आया हो, रसीद एक ही।",
  "Amount (part": "राशि (हिस्सा",
  cash: "नक़द",
  cheque: "चेक",
  "bank transfer": "बैंक ट्रांसफ़र",
  upi: "यू पी आई",
  card: "कार्ड",
  "net banking": "नेट बैंकिंग",
  issued: "जारी",
  "partially paid": "आंशिक भुगतान",
  paid: "भुगतान हुआ",
  overdue: "अवधि पार",
  draft: "मसौदा",
  cancelled: "रद्द",
  // --- competitions and results --------------------------------------------
  Competitions: "प्रतियोगिताएँ",
  "Olympiads, scholarship tests and everything the school did not set.":
    "ओलंपियाड, छात्रवृत्ति परीक्षाएँ, और वह सब जो स्कूल ने नहीं रखा।",
  "Honours this year": "इस वर्ष की उपलब्धियाँ",
  "An award, or a rank at city level or better. Somebody is always first in the school.":
    "कोई पुरस्कार, या शहर स्तर या उससे ऊपर का रैंक। स्कूल में पहला तो कोई न कोई आता ही है।",
  "Nothing on the board yet this year.": "इस वर्ष अभी बोर्ड पर कुछ नहीं।",
  Entries: "प्रविष्टियाँ",
  "Children taking part": "भाग लेने वाले बच्चे",
  "Add a competition": "प्रतियोगिता जोड़ें",
  "Its rounds come with it — a competition with no round cannot hold a result.":
    "इसके चरण साथ ही बनते हैं — बिना चरण वाली प्रतियोगिता में परिणाम रखा ही नहीं जा सकता।",
  "Nothing entered yet": "अभी कुछ दर्ज नहीं",
  "Add an olympiad or a scholarship test, then enter children for it.":
    "कोई ओलंपियाड या छात्रवृत्ति परीक्षा जोड़िए, फिर उसमें बच्चों को दर्ज कीजिए।",
  "Pick one to see who is entered.": "देखिए किसमें कौन दर्ज है — कोई एक चुनिए।",
  "Nobody entered yet": "अभी कोई दर्ज नहीं",
  "Enter children from their class list or from a section.":
    "कक्षा की सूची से या किसी सेक्शन से बच्चों को दर्ज कीजिए।",
  "Not entered for anything yet": "अभी किसी में दर्ज नहीं",
  "Olympiads and other competitions this child sits will appear here.":
    "यह बच्चा जिन ओलंपियाड और प्रतियोगिताओं में बैठेगा, वे यहाँ दिखेंगी।",
  "Entered — result awaited": "दर्ज है — परिणाम प्रतीक्षित",
  "Result awaited": "परिणाम प्रतीक्षित",
  "National rank": "राष्ट्रीय रैंक",
  "State rank": "राज्य रैंक",
  "City rank": "शहर रैंक",
  "School rank": "स्कूल रैंक",
  Qualified: "अगले चरण में",
  "Did not qualify": "अगले चरण में नहीं",
  "Reg. no.": "पंजीकरण क्र.",
  Olympiad: "ओलंपियाड",
  "Scholarship test": "छात्रवृत्ति परीक्षा",
  "Competitive exam": "प्रतियोगी परीक्षा",
  Sports: "खेल",
  Cultural: "सांस्कृतिक",
  Kind: "प्रकार",
  Organiser: "आयोजक",
  "Adding…": "जोड़ा जा रहा है…",
  "No academic year yet": "अभी कोई शैक्षणिक वर्ष नहीं",
  "Create an academic year before entering children for anything.":
    "बच्चों को कहीं दर्ज करने से पहले शैक्षणिक वर्ष बनाइए।",
  "Loading competitions": "प्रतियोगिताएँ लोड हो रही हैं",

  // Results on a profile
  Marks: "अंक",
  Percentage: "प्रतिशत",
  "Published results only. A subject appears once the school releases it.":
    "सिर्फ़ प्रकाशित परिणाम। कोई विषय तभी दिखता है जब स्कूल उसे जारी करता है।",
  "No published results": "कोई प्रकाशित परिणाम नहीं",
  "Results appear here once the school releases the term.":
    "स्कूल जैसे ही सत्र जारी करता है, परिणाम यहाँ दिख जाते हैं।",
  "How the section is doing": "सेक्शन कैसा चल रहा है",
  "Published results only, every subject at once.":
    "सिर्फ़ प्रकाशित परिणाम, सारे विषय एक साथ।",
  "Section average": "सेक्शन का औसत",
  "Nothing published for this term": "इस सत्र का कुछ प्रकाशित नहीं",
  "Marks appear here once the school releases the papers.":
    "स्कूल जैसे ही पेपर जारी करता है, अंक यहाँ दिख जाते हैं।",
  "Exam term": "परीक्षा सत्र",
  "View register": "हाज़िरी देखें",
  "Take register": "हाज़िरी लें",
  present: "उपस्थित",
  absent: "अनुपस्थित",
  // --- practice ------------------------------------------------------------
  Practice: "अभ्यास",
  "Practice papers for your class. These do not go on your report card.":
    "आपकी कक्षा के अभ्यास पेपर। ये रिपोर्ट कार्ड पर नहीं जाते।",
  "Questions are approved once and assembled into papers many times. Practice does not go on a report card.":
    "सवाल एक बार मंज़ूर होते हैं और उन्हीं से बार-बार पेपर बनते हैं। अभ्यास रिपोर्ट कार्ड पर नहीं जाता।",
  "This is practice. It does not go on your report card.":
    "यह अभ्यास है। यह आपके रिपोर्ट कार्ड पर नहीं जाता।",
  "Nothing to practise yet": "अभी अभ्यास के लिए कुछ नहीं",
  "Your teacher will publish a paper when one is ready.":
    "जब पेपर तैयार होगा, आपके शिक्षक उसे जारी कर देंगे।",
  Start: "शुरू करें",
  "Carry on": "जारी रखें",
  Started: "शुरू किया",
  Closed: "बंद",
  Closes: "बंद होगा",
  "This paper has closed.": "यह पेपर बंद हो चुका है।",
  "See the answers": "जवाब देखें",
  "Hand it in": "जमा करें",
  "Back to practice": "अभ्यास पर वापस",
  "Loading the paper": "पेपर लोड हो रहा है",
  "Loading practice papers": "अभ्यास पेपर लोड हो रहे हैं",
  "Loading practice": "अभ्यास लोड हो रहा है",
  questions: "सवाल",
  minutes: "मिनट",
  left: "बचे",
  answered: "के जवाब दिए",
  of: "में से",
  right: "सही",
  Correct: "सही",
  Wrong: "ग़लत",
  "Your result": "आपका नतीजा",
  Score: "अंक",
  Right: "सही",

  // The teacher's side
  "Class and subject": "कक्षा और विषय",
  "Everything below follows this choice.": "नीचे सब कुछ इसी चुनाव पर चलता है।",
  "Add questions to the bank": "बैंक में सवाल जोड़ें",
  "Written for your class and your board. They arrive unread — nothing reaches a child until you approve them.":
    "आपकी कक्षा और आपके बोर्ड के लिए लिखे जाते हैं। ये बिना पढ़े आते हैं — आपकी मंज़ूरी के बिना कोई बच्चे तक नहीं पहुँचता।",
  Topics: "विषय-वस्तु",
  "Comma separated. Leave empty for the syllabus so far.":
    "कॉमा से अलग करें। खाली छोड़ें तो अब तक का पाठ्यक्रम।",
  "How many": "कितने",
  "Write questions": "सवाल लिखवाएँ",
  "Writing…": "लिखे जा रहे हैं…",
  "A wrong answer key teaches a child something false. Read every one.":
    "एक ग़लत जवाब बच्चे को ग़लत चीज़ सिखा देता है। हर सवाल पढ़िए।",
  "Waiting for you": "आपकी मंज़ूरी का इंतज़ार",
  "Read the question, the answer and the explanation. Approve only what you would set yourself.":
    "सवाल, जवाब और समझाइश तीनों पढ़िए। वही मंज़ूर कीजिए जो आप ख़ुद पूछते।",
  "Nothing waiting": "कुछ इंतज़ार में नहीं",
  "Everything in the bank for this class has been read.":
    "इस कक्षा के बैंक में जो था, सब पढ़ा जा चुका है।",
  Approve: "मंज़ूर करें",
  "Approve all": "सब मंज़ूर करें",
  "Reject all": "सब अस्वीकार करें",
  Level: "स्तर",
  correct: "सही",
  "Make a paper": "पेपर बनाएँ",
  "Make paper": "पेपर बनाएँ",
  "Drawn from the approved bank. No new questions are written.":
    "मंज़ूर बैंक से लिया जाता है। कोई नया सवाल नहीं लिखा जाता।",
  Questions: "सवाल",
  Papers: "पेपर",
  "Pick one to see how it went.": "देखिए कैसा रहा — कोई एक चुनिए।",
  "No papers yet": "अभी कोई पेपर नहीं",
  "Approve some questions, then make a paper from them.":
    "कुछ सवाल मंज़ूर कीजिए, फिर उनसे पेपर बनाइए।",
  "Publish to students": "विद्यार्थियों को दें",
  "Weakest topics first": "सबसे कमज़ोर विषय-वस्तु पहले",
  "Nobody has sat it yet": "अभी किसी ने नहीं दिया",
  results: "नतीजे",
  submitted: "जमा",
  "in review": "समीक्षा में",
  published: "जारी",
  closed: "बंद",
  "in progress": "चल रहा है",
  "Create an academic year before setting practice papers.":
    "अभ्यास पेपर बनाने से पहले शैक्षणिक वर्ष बनाइए।",
  "Practice papers sat on the portal. These do not go on a report card.":
    "पोर्टल पर दिए गए अभ्यास पेपर। ये रिपोर्ट कार्ड पर नहीं जाते।",
  "No practice yet": "अभी कोई अभ्यास नहीं",
  "Papers this child has sat will appear here once they hand one in.":
    "यह बच्चा जो पेपर देगा, जमा करते ही वे यहाँ दिखेंगे।",

  // --- the sign-in screen ---
  "Run the whole school from one place.": "पूरा स्कूल एक ही जगह से चलाइए।",
  "One record for every child, from the enquiry form to the leaving certificate. Built for Indian schools.":
    "हर बच्चे का एक ही रिकॉर्ड — पूछताछ फ़ॉर्म से लेकर स्कूल छोड़ने के प्रमाणपत्र तक। भारतीय स्कूलों के लिए बनाया गया।",
  "Admissions, attendance and timetable": "प्रवेश, उपस्थिति और समय-सारणी",
  "Fees, receipts and payment reminders": "फ़ीस, रसीदें और भुगतान की याद",
  "Exams, report cards and staff payroll": "परीक्षाएँ, रिपोर्ट कार्ड और वेतन",
  "Use the email or phone number your school has on record.":
    "वही ईमेल या फ़ोन नंबर डालिए जो स्कूल के रिकॉर्ड में है।",
  "Trouble signing in? Ask your school office.":
    "साइन इन नहीं हो पा रहा? स्कूल कार्यालय से पूछिए।",
};
